import { useEffect, useState, useCallback } from "react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { DeleteIcon } from "@chakra-ui/icons";
import toast from "react-hot-toast";

import {
  HeartReact,
  SnapReact,
  FistReact,
  ClappingReact,
} from "../../Assets/SvgIcons";
import { useLazyGetOrganizationDetailsQuery, useViewNotificationsMutation, useDeleteNotificationsMutation } from "../../../redux/reducers/apiSlice";
import {
  MenuItem,
  IconContainer,
  ReactionContainer,
  SwipeableNotificationWrap,
  SwipeableContent,
  SwipeDeleteStrip,
} from "./NotificationStyles";
import { Button } from "@chakra-ui/react";
import AcceptInviteModal from "../../Components/Modal/AcceptInviteModal";
import NotificationMessage from "./NotificationMessage";
import useScreenType from "react-screentype-hook";

const keywordToSVG = {
  HEART: <HeartReact />,
  SNAP: <SnapReact />,
  "HIGH-FIVE": <FistReact />,

  CLAP: <ClappingReact />,
  FIST: <FistReact />,
};

const SWIPE_DELETE_THRESHOLD = 70;

export const Notification = ({
  notification,
}) => {
  const navigate = useNavigate();
  const [getOrganizationDetails, { data: orgData }] = useLazyGetOrganizationDetailsQuery();
  const [viewNotifications] = useViewNotificationsMutation();
  const [deleteNotification] = useDeleteNotificationsMutation();
  const [isOpenAcceptInviteModal, setIsOpenAcceptInviteModal] = useState(false);
  const [swipeX, setSwipeX] = useState(0);
  const [touchStartX, setTouchStartX] = useState(null);
  const { isMobile } = useScreenType();

  useEffect(() => {
    const getOrgName = async () => {
      if (notification?.orgId) {
        getOrganizationDetails(notification.orgId);
      }
    };
    getOrgName();
  }, []);

  const handleDelete = useCallback(async (e) => {
    if (e) e.stopPropagation();
    try {
      await deleteNotification({ id: notification.id }).unwrap();
      toast.success("Notification removed");
    } catch (err) {
      toast.error("Could not remove notification");
    }
  }, [notification.id, deleteNotification]);

  const handleNotificationClick = useCallback(async () => {
    if (notification.data?.postId) {
      if (notification.data?.interactionId) {
        navigate(`/posts/${notification.data.postId}/interactions/${notification.data.interactionId}?showComment=true`);
      } else {
        navigate(`/posts/${notification.data.postId}`);
      }
    } else if (notification.data?.orgId) {
      navigate(`/organizations/${notification.orgId}`);
    } else if (notification.userId) {
      navigate(`/users/${notification.userId}`);
    }

    try {
      await viewNotifications({ id: notification.id });
      toast.success("Marked as read");
    } catch (err) {
      toast.error("Something went wrong marking the notification as read");
    }
  }, [notification, navigate, viewNotifications]);

  const onTouchStart = (e) => {
    setTouchStartX(e.touches[0].clientX);
  };
  const onTouchMove = (e) => {
    if (touchStartX == null) return;
    const diff = e.touches[0].clientX - touchStartX;
    setSwipeX(Math.min(0, diff));
  };
  const onTouchEnd = () => {
    if (swipeX <= -SWIPE_DELETE_THRESHOLD) {
      handleDelete();
    }
    setTouchStartX(null);
    setSwipeX(0);
  };

  const match = notification?.message?.toLowerCase()?.match(/\b(heart|snap|fist|high-five)\b\s*$/)?.[1];

  const itemBackground = notification.status === "VIEWED" ? "transparent" : "rgba(0,0,255,0.05)";

  // Mobile / touch: swipe-to-delete + arrow
  if (isMobile) {
    return (
      <SwipeableNotificationWrap style={{ background: itemBackground }}>
        {swipeX < 0 && <SwipeDeleteStrip>Delete</SwipeDeleteStrip>}
        <SwipeableContent
          style={{ transform: `translateX(${swipeX}px)` }}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >
          <MenuItem style={{ background: itemBackground }} onClick={handleNotificationClick}>
            <div className="menu-wrapper">
              <div className="time-ago">
                {notification?.createdAt &&
                  formatDistanceToNow(new Date(notification.createdAt), {
                    addSuffix: true,
                  }).replace("about", "")}
              </div>
              <div className="row">
                <NotificationMessage setIsOpenAcceptInviteModal={setIsOpenAcceptInviteModal} notification={notification} />
                {notification.data?.interactionId && match && (
                  <ReactionContainer>
                    {keywordToSVG[match?.toUpperCase()]}
                  </ReactionContainer>
                )}
                <div className="icons">
                  {notification?.message?.toLowerCase().includes("invited you to join") && (
                    <div style={{ display: "flex", justifyContent: "flex-start" }}>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsOpenAcceptInviteModal(true);
                        }}
                        style={{ backgroundColor: "transparent", color: "rgb(238,90,41)" }}
                      >
                        View invitation
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </MenuItem>
        </SwipeableContent>
        {orgData?.name && (
          <AcceptInviteModal
            isOpen={isOpenAcceptInviteModal}
            onClose={() => setIsOpenAcceptInviteModal(false)}
            organizationName={orgData?.name}
            notification={notification}
          />
        )}
      </SwipeableNotificationWrap>
    );
  }

  return (
    <MenuItem style={{ background: itemBackground }} onClick={handleNotificationClick}>
      <div className="menu-wrapper">
        <div className="time-ago">
          {notification?.createdAt &&
            formatDistanceToNow(new Date(notification.createdAt), {
              addSuffix: true,
            }).replace("about", "")}
        </div>
        <div className="row">
          <NotificationMessage setIsOpenAcceptInviteModal={setIsOpenAcceptInviteModal} notification={notification} />
          {notification.data?.interactionId && match && (
            <ReactionContainer>
              {keywordToSVG[match?.toUpperCase()]}
            </ReactionContainer>
          )}
          <div className="icons">
            {notification?.message?.toLowerCase().includes("invited you to join") && (
              <div style={{ display: "flex", justifyContent: "flex-start" }}>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpenAcceptInviteModal(true);
                  }}
                  style={{ backgroundColor: "transparent", color: "rgb(238,90,41)" }}
                >
                  View invitation
                </Button>
              </div>
            )}
            <IconContainer>
              <button
                type="button"
                onClick={(e) => handleDelete(e)}
                aria-label="Delete notification"
                style={{ paddingLeft: 4, color: "#ED3724" }}
              >
                <DeleteIcon boxSize={4} />
              </button>
            </IconContainer>
          </div>
        </div>
      </div>
      {orgData?.name && (
        <AcceptInviteModal
          isOpen={isOpenAcceptInviteModal}
          onClose={() => setIsOpenAcceptInviteModal(false)}
          organizationName={orgData?.name}
          notification={notification}
        />
      )}
    </MenuItem>
  );
};
